import { Row, Col, Avatar, Image } from 'antd';
import { useNavigate } from 'react-router-dom';
import CustomModal from '../../components/Modal/CustomModal';
import React, { useCallback, useEffect, useMemo, useState, useRef } from 'react';
import { useShell } from '../../components/Layout/ChatShell';
import PaneBack from '../../components/Layout/PaneBack';
import { useBackGuard } from '../../hooks/useBackGuard';
import { useSharedMedia } from '../../hooks/useSharedMedia';
import {
  useInfiniteList,
  useScrollAnchor,
  useStreamBottomOnOpen,
} from '../../hooks/useInfiniteList';
import { usePresence, PRESENCE } from '../../hooks/usePresence';
import MessageMenu from '../../components/ChatLayout/MessageMenu';
import MessageMenuTooltip from '../../components/ChatLayout/MessageMenuTooltip';
import DayDivider from '../../components/ChatLayout/DayDivider';
import './Messages.css';
import { Text } from '../../components';
import { Colors, Utils } from '../../config';
import { MsgAction } from '../../store/actions';
import { useDispatch, useSelector } from 'react-redux';
import ReactPlayer from 'react-player';
import { useForm, Controller } from 'react-hook-form';
import { Loader, Skeleton } from '../../components';
// React Icons
import { CiVideoOn } from 'react-icons/ci';
import { IoCallOutline, IoChevronBackCircle } from 'react-icons/io5';
import { UserOutlined } from '@ant-design/icons';
import { GiSoundWaves } from 'react-icons/gi';
import { MdCancel, MdReportProblem, MdInfoOutline } from 'react-icons/md';
import { FaAudioDescription } from 'react-icons/fa6';
import ReactAudioPlayer from 'react-audio-player';
import SocketAPI, { isRealtimeUsable, SOCKET_DOWN_MESSAGE } from '../../config/socket/socket';
import MessageReactions from '../../components/ChatLayout/MessageReactions';
import ReactionPicker from '../../components/ChatLayout/ReactionPicker';
import MessageText from '../../components/ChatLayout/MessageText';
import DirectComposer from '../../components/ChatLayout/DirectComposer';
import TypingIndicator from '../../components/ChatLayout/TypingIndicator';
import MessageTicks from '../../components/ChatLayout/MessageTicks';
import ScrollToLatest from '../../components/ChatLayout/ScrollToLatest';
import PlaceholderAvatar from '../../components/ChatLayout/PlaceholderAvatar';
import {
  InfoHeader,
  InfoBody,
  InfoIdentity,
  InfoStatusPill,
  InfoActions,
  InfoAction,
  InfoField,
  InfoReadout,
  InfoSection,
  MediaGrid,
  MediaTile,
} from '../../components/ChatLayout/InfoPanel';
import emojiSound from './emojiSound.mp3';
import { MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE } from '../../store/constants';
import { dayDividerFor, formatLastSeen, formatLastSeenShort } from '../../config/util';
import { REPORT_LIMITS, ROUTES, SCHEDULE_LIMITS, TYPING_EXPIRY_MS } from '../../config/constants';
import { applyReactionEvent } from '../../config/util/reactions';
import REACTION_EMOJIS from '../../config/emojis';

/**
 * Attachment cap, enforced in the effects that watch each queue. The upload
 * handlers also reject beyond this, so the constant is shared rather than
 * repeated as a literal in five places.
 */
const MAX_ATTACHMENTS = 5;

/** Stable empty stream — see `PersonalChatMsgs`. */
const EMPTY_STREAM = [];

const MessagesChat = () => {
  const { isPhone, rightSideOpen, setRightSideOpen } = useShell();

  // On a phone the details panel covers the conversation, so Back has to close
  // it rather than leave the chat.
  useBackGuard(isPhone && rightSideOpen ? 'details' : null, () => setRightSideOpen(false));
  const socket = SocketAPI();
  // use Dispatch
  const dispatch = useDispatch();

  // use Navigate
  const navigate = useNavigate();

  // use Ref
  const lastMessageRef = useRef(null);
  const ScrollingRef = useRef(null);
  const ringtoneRef = useRef(null);

  // use Selector
  const userData = useSelector((state) => state?.AppReducer?.user?.data);

  /**
   * One subscription per value, not one for the whole slice.
   *
   * This was `useSelector((state) => state.MsgReducer)` destructured into the five
   * names below. That selector returns the slice OBJECT, so react-redux's reference
   * check fired whenever any of its ~60 keys changed — not just these five. A
   * notification arriving, a call-history page landing, an unread-count tick, a
   * shared-media fetch or a saved-message toggle each forced a full re-render of this
   * component and every message bubble under it.
   *
   * Selecting each value on its own means a re-render only when a value this pane
   * actually reads has changed.
   */
  const selected_chat_user = useSelector((state) => state?.MsgReducer?.selected_chat_user);
  const message_chats = useSelector((state) => state?.MsgReducer?.message_chats);
  const message_chats_loader = useSelector((state) => state?.MsgReducer?.message_chats_loader);
  const message_chats_channel_id = useSelector(
    (state) => state?.MsgReducer?.message_chats_channel_id
  );
  // `message_count` and `messages_length` no longer take part in paging — the guard is
  // the server's cursor, not a comparison between a total and a page length. They are
  // left on the slice for anything that wants to display a total.
  const SelectedChatUser = selected_chat_user;
  // Fixed set of unicode reaction emojis (see config/emojis) — each entry is the
  // emoji character itself, stored directly in chat_reactions.emoji_id.
  const EmojiLists = REACTION_EMOJIS;

  // use Form
  const { handleSubmit, control, reset } = useForm({});

  // use State
  const [modal, setModal] = useState(false);
  const [reportMsgId, setReportMsgId] = useState(null);
  const [newMessageRecieve, setNewMessageRecieve] = useState(false);
  const [, setMessageDeleted] = useState(null);
  const [typing, setTyping] = useState();
  const [selectedImages, setSelectedImages] = useState([]);
  // Boolean, not the string 'show' it used to hold — it drives an
  // `aria-expanded` and a `!` toggle, and a truthy string breaks both.
  const [showHideMedia, setShowHideMedia] = useState(true);
  const [replyOnMsg, setReplyOnMsg] = useState(null); // set the hover state
  const [unreadMsg, setUnreadMsg] = useState(0);
  const [currentIndex, setCurrentIndex] = useState(null);
  const [reactedListModal, setReactedListModal] = useState(false);
  const [reactedMembersList, setReactedMembersList] = useState(null);
  //schedule message states
  const [isScheduleModalOpen, setIsScheduleModalOpen] = useState(false);
  const [scheduleDate, setScheduleDate] = useState('');
  const [scheduleTime, setScheduleTime] = useState('');
  const [scheduleMessage, setScheduleMessage] = useState('');

  const channel_id = SelectedChatUser?.id;

  /**
   * The stream, but only when it is THIS conversation's.
   *
   * The sidebar selects on click and the pane clears on the route commit that follows,
   * so for one commit `channel_id` is the conversation being opened while the rows are
   * still the one being left. Reading them as this channel's is what made switching
   * conversations open on old messages and kill the scroll-up pager (see
   * `message_chats_channel_id` in the reducer). Rendering them is wrong for the same
   * reason: for that commit the pane showed someone else's conversation under this
   * conversation's header.
   *
   * `EMPTY_STREAM` rather than a fresh `[]`, so the identity is stable and every
   * dependency list keyed on this array stays quiet.
   */
  const streamIsCurrent =
    Boolean(channel_id) && String(message_chats_channel_id) === String(channel_id);
  const PersonalChatMsgs = streamIsCurrent ? message_chats : EMPTY_STREAM;
  const peerId =
    SelectedChatUser?.chat_member?.member_id ?? SelectedChatUser?.chat_member?.user?.id;

  /**
   * The other participant's presence, from the one shared map.
   *
   * This was a local `isUserOnline` boolean fed by `online:user-list`, and it was wrong
   * in three ways at once: it started `false` and only ever moved on a broadcast — which
   * fires on somebody ELSE's connect — so an online peer read as offline until an
   * unrelated user happened to connect; it never reset on switching conversation, so a
   * stale green dot followed you from an online peer to an offline one; and it disagreed
   * with the sidebar, which derived its own answer from `last_seen` with its own
   * thresholds. `usePresence` subscribes to this one peer and seeds from the ack.
   */
  const peerIds = useMemo(() => (peerId ? [peerId] : []), [peerId]);
  const { statusOf, lastSeenOf } = usePresence(socket, peerIds);
  const peerStatus =
    statusOf(peerId) ?? SelectedChatUser?.chat_member?.user?.user_online_status ?? PRESENCE.OFFLINE;
  const isUserOnline = peerStatus === PRESENCE.ONLINE;

  /**
   * Whether the OTHER side of this conversation is typing.
   *
   * `typing:alert` is broadcast per channel and echoes back to the sender, so
   * all three guards carry weight: the flag must be live, it must belong to the
   * channel on screen, and it must not be this user's own keystrokes.
   */
  const isPeerTyping = Boolean(
    typing?.status && typing?.channel_id === channel_id && typing?.sender_id !== userData?.id
  );

  // Server cursor for the next (older) page. `null` after a load is the only signal the
  // pager treats as "there is nothing older" — `message_count` is advisory, and a client
  // that stops on `loaded >= count` never stops when the count runs ahead of what the
  // server will actually serve.
  const messageNextCursor = useSelector((state) => state?.MsgReducer?.message_next_cursor);

  /**
   * When the other participant had read everything up to — the timestamp the double tick
   * means. Server-computed from `chat_members.last_read_at`, seeded by the history read
   * and advanced by receipts, so it survives a reload and a remount of this pane.
   */
  const othersReadAt = useSelector((state) => state?.MsgReducer?.others_read_at?.[channel_id]);

  // Holds the viewport still when a page is prepended: `scrollHeight` grows while
  // `scrollTop` does not, so without this the reader lands somewhere else in the
  // conversation — and, being left pinned near the top, immediately triggers another
  // page. The fix was present in the file as two commented-out lines.
  const captureAnchor = useScrollAnchor(ScrollingRef, PersonalChatMsgs?.length);

  // Opens the conversation on its newest message rather than on the oldest message of
  // the newest page. Keyed on the channel, so switching conversation re-arms it and a
  // prepended page — held still by the anchor above — does not trip it.
  useStreamBottomOnOpen(ScrollingRef, {
    key: channel_id,
    count: PersonalChatMsgs?.length ?? 0,
  });
  const is_last_seen_enabled = SelectedChatUser?.chat_member?.user?.last_seen_enabled;

  // Shared media for the details panel: the first page when it opens, further
  // pages as it is scrolled. The hand-rolled version this replaces could fire a
  // burst of identical requests per scroll gesture and then sit on a spinner
  // that never resolved — see the hook for why.
  const {
    media: shareMedia,
    onScroll: handleMediaScroll,
    loading: shareMediaLoading,
  } = useSharedMedia({ channelId: channel_id, open: rightSideOpen });

  // The "newest own message only" scan that used to live here is gone with the
  // "· Seen" text it existed for: delivery is now a tick on every outgoing
  // message (MessageTicks), computed per message from the same read receipt.

  /**
   * Zero this channel's unread count and refresh what the read changed.
   *
   * Three things were wrong with the version this replaces.
   *
   *  • It emitted onto a possibly PARKED socket. A connection rejected at the handshake
   *    has `active: false` with nothing scheduled to retry, and `emit` on it neither
   *    throws nor reports — the packet goes into a buffer that is never flushed. So the
   *    ack never came, the badge stayed on screen forever, and the peer never got the
   *    receipt. Every other write in this app already guards with `isRealtimeUsable`.
   *  • It ignored the ack's `success`. The server refuses a read for a channel the user
   *    is not in, and the client cleared the badge anyway.
   *  • It refreshed the sidebar with `GlobalSearchChatsGroups()` — no arguments, so
   *    `search: undefined` and no cursor, which the middleware turns into `replace: true`.
   *    That cleared any active search and discarded every page the reader had scrolled
   *    through. And because this runs again on every inbound message (see the
   *    `message:receive` handler), it did that repeatedly for as long as the conversation
   *    stayed open. `ChannelRead` patches the one row instead.
   */
  const markChannelRead = useCallback(() => {
    if (!channel_id || !userData?.id) return;
    // A chat left open in a background tab is not being read: without this the
    // count is cleared for messages nobody ever looked at. The visibilitychange
    // effect below flushes the read as soon as the tab comes back.
    if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
    if (!isRealtimeUsable(socket)) return;

    socket.emit('message:read', { channel_id }, (ack) => {
      if (ack && ack.success === false) return;
      dispatch(
        MsgAction.ChannelRead({ channel_id, others_read_at: ack?.others_read_at ?? undefined })
      );
      // The global "Messages N" nav total is a separate small read and is not pushed.
      dispatch(MsgAction.UnreadMessageDot());
    });
  }, [socket, channel_id, userData?.id, dispatch]);

  //use Effect
  useEffect(() => {
    markChannelRead();

    return () => {
      setUnreadMsg(0);
    };
  }, [markChannelRead]);

  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') markChannelRead();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, [markChannelRead]);

  /**
   * When the other participant had read everything up to.
   *
   * Seeded from the history read (`others_read_at`, computed server-side from
   * `chat_members.last_read_at`) and advanced by receipts. Both halves matter: without
   * the seed this was reset to `null` on every mount, so reopening a conversation showed
   * a single tick on messages the recipient had read days earlier — the column did not
   * exist, so there was nothing to seed from.
   *
   * The receipt carries `others_read_at` rather than a bare `read_at`, so a group means
   * "everyone else has read" without the client having to know the roster.
   */
  useEffect(() => {
    if (!socket) return undefined;

    const onReadAlert = (resp) => {
      if (String(resp?.channel_id) !== String(channel_id)) return;
      if (resp?.user_id === userData?.id) return;
      dispatch(
        MsgAction.ChannelReadReceipt({ channel_id, others_read_at: resp?.others_read_at ?? null })
      );
    };

    socket.on('message:read:alert', onReadAlert);
    return () => socket.off('message:read:alert', onReadAlert);
  }, [socket, channel_id, userData?.id, dispatch]);

  useEffect(() => {
    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [newMessageRecieve]);

  /**
   * Follow the typing bubble.
   *
   * It mounts at the tail of the stream, below the newest message, and nothing
   * was scrolling for it — the two effects that scroll are keyed on a message
   * arriving and on mount — so it appeared just off the bottom of the viewport
   * and had to be scrolled to by hand.
   *
   * Only when the reader is already at the end. The check runs after the bubble
   * is in the DOM, so `scrollHeight` already counts it: at-the-bottom reads as
   * roughly the bubble's own height, and the 200px allowance covers that while
   * still being far short of "reading history". Yanking someone out of the
   * history they are reading to show a typing indicator would be worse than not
   * showing it at all.
   */
  useEffect(() => {
    if (!isPeerTyping) return;

    const node = ScrollingRef.current;
    if (!node) return;

    const distanceFromBottom = node.scrollHeight - node.scrollTop - node.clientHeight;
    if (distanceFromBottom > 200) return;

    lastMessageRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [isPeerTyping]);

  useEffect(() => {
    if (selectedImages.length > 0) {
      const formData = new FormData();
      for (const key in selectedImages) {
        formData.append('media', selectedImages[key]);
      }
      dispatch(MsgAction.UploadChatMedia(formData));
    }
  }, [selectedImages]);

  useEffect(() => {
    setUnreadMsg(SelectedChatUser?.chat_member?.unread_message_count);
  }, [SelectedChatUser?.chat_member?.unread_message_count]);

  /**
   * Join this channel's room.
   *
   * The room is what the server broadcasts inbound messages to
   * (`socket.broadcast.to(channel_id)`), and it is per CONNECTION — so it has to
   * be joined when the pane opens AND re-joined after any reconnect.
   *
   * This used to subscribe to `connect` and nothing else. The connection is
   * opened once at app boot (App.js), so by the time a conversation is opened by
   * client-side navigation `connect` has already fired and never fires again:
   * the room was only ever joined when a full page load happened to put the
   * connect after this listener. That is why the pane went quiet until a
   * refresh — the sidebar kept updating, because its `message:alert` is sent to
   * the reader's personal room rather than the channel's.
   */
  useEffect(() => {
    if (!socket || !channel_id) return undefined;

    const join = () => socket.emit('join', { channel_id });

    if (socket.connected) join();
    socket.on('connect', join);
    // Removed by reference — the connection is shared, so a bare
    // `off('connect')` would also drop the group pane's join. The old teardown
    // additionally called `off('join')`, which removed nothing: `join` is
    // emitted, never subscribed.
    return () => socket.off('connect', join);
  }, [socket, channel_id]);

  useEffect(() => {
    dispatch(MsgAction.EmptyCallHistory());
    dispatch(MsgAction.UnreadMessageDot());
    // No scroll here: on mount the stream is empty, so this could only ever have been
    // a no-op. `useStreamBottomOnOpen` above pins once the first page is rendered.
  }, []);

  useEffect(() => {
    if (!socket) return undefined;

    const onMessageReceive = (item) => {
      if (item?.sender_id != userData?.id) {
        const reversedArr = [
          {
            id: item?.id,
            channel_id: item?.channel_id,
            reply_id: item?.reply_id,
            replied_message: item?.replied_message,
            sendMsgTime: item?.createdAt,
            message: item?.message,
            type: item?.type,
            media: item?.media,
            createdAt: item?.updatedAt,
            statusMsg: item?.status,
            sender_id: item?.sender_id,
            sender: item?.sender,
            status: item?.status,
            chat_reactions: item?.chat_reactions,
          },
        ];
        dispatch(MsgAction.SendMsg(reversedArr));
        setNewMessageRecieve((prev) => !prev);

        // You are looking at this conversation, so the message arrives already
        // read. Without this the server keeps counting up and the sidebar badge
        // climbs while the chat sits open — it only cleared on switching away.
        if (String(item?.channel_id) === String(channel_id)) {
          markChannelRead();
        }
      }
      // Whoever was typing has now sent, so drop the indicator. This used to call
      // `socket.off('typing:alert')` — it removed the LISTENER rather than
      // clearing the state, and since that effect only re-runs when the socket
      // changes, the typing indicator stayed dead for the rest of the session
      // after the first message arrived.
      setTyping(null);
    };

    socket.on('message:receive', onMessageReceive);
    return () => socket.off('message:receive', onMessageReceive);
  }, [socket, channel_id, userData?.id, dispatch, markChannelRead]);

  useEffect(() => {
    if (!socket) return undefined;

    let expiry;

    /**
     * A received indicator expires on its own.
     *
     * `status: false` was the only thing that cleared this, and it cannot be
     * relied on: the sender's tab can close, their connection can drop, they can
     * leave text sitting in the composer and walk away — and an older client does
     * not emit it at all. Any of those left "typing…" on screen indefinitely.
     *
     * Every fresh signal restarts the window, so continuous typing keeps it lit.
     */
    const onTyping = (resp) => {
      clearTimeout(expiry);
      setTyping(resp);
      if (resp?.status) expiry = setTimeout(() => setTyping(null), TYPING_EXPIRY_MS);
    };

    socket.on('typing:alert', onTyping);
    // This effect returned nothing, so the listener survived unmount and a second
    // copy was added on every remount.
    return () => {
      clearTimeout(expiry);
      socket.off('typing:alert', onTyping);
    };
  }, [socket]);

  const reactedMembersListClose = () => {
    setReactedListModal(false);
    setReactedMembersList(null);
  };

  const reactedListingModal = (members) => {
    setReactedListModal(true);
    setReactedMembersList(members);
  };

  /**
   * Latest stream, readable from the socket handler without being a dependency of it.
   * See the equivalent note in GroupMessages: listing the array here re-subscribed the
   * listener on every inbound message, and handed the handler a stale snapshot.
   */
  const chatMsgsRef = useRef(PersonalChatMsgs);
  chatMsgsRef.current = PersonalChatMsgs;

  useEffect(() => {
    if (!socket) return undefined;

    const onReaction = (data) => {
      const held = chatMsgsRef.current;
      if (!held?.length) return;
      setNewReactionMessages(applyReactionEvent(held, data));
    };

    socket.on('emoji:receive', onReaction);
    // Never removed before the ref refactor, so handlers accumulated one per
    // conversation opened — after switching chats five times a single reaction was
    // applied five times.
    return () => socket.off('emoji:receive', onReaction);
  }, [socket]);

  // Switching conversation drops the details panel. Keyed on the id, not on the
  // object: `selected_chat_user` is replaced in the store on every refresh of
  // the conversation, so keyed on identity this closed the panel under the user
  // while they were reading it.
  useEffect(() => {
    setRightSideOpen(false);
  }, [channel_id, setRightSideOpen]);

  /**
   * Cap the attachment queue.
   *
   * This used to assign `selectedImages.length = 5`, which truncates the array in
   * place without changing its identity: React saw no new value, never
   * re-rendered, and the effect keyed on that same array never re-ran. The user
   * was told about a limit that stayed invisible — every thumbnail remained on
   * screen while the array behind it held five. `slice` returns a new array, so
   * the tray and the payload agree. The message goes through the app's toast
   * rather than a blocking `alert`.
   */
  useEffect(() => {
    if (selectedImages?.length > MAX_ATTACHMENTS) {
      Utils.showMessage('warning', `You can attach up to ${MAX_ATTACHMENTS} files at a time.`);
      setSelectedImages((prev) => prev.slice(0, MAX_ATTACHMENTS));
    }
  }, [selectedImages]);

  // Enter-to-send belongs to the composer, and the composer's <Input> already
  // owns it via `onPressEnter` — with the guards this needs (`canSend`, not
  // uploading, not recording). There used to be a `keydown` listener on
  // `document` here as well, which meant two things: the same keystroke fired
  // both handlers so every keyboard send emitted `message:send` twice, and
  // because the listener was global with no target check, pressing Enter in the
  // search field or any modal dispatched the current draft.

  // Functions
  /**
   * Place a 1:1 call. The created call's id goes straight into the URL — that is
   * what makes the call survive a reload, so it must not be dropped here.
   */
  const placeCall = (type) => {
    const Data = {
      receiver_ids: [SelectedChatUser?.chat_member?.member_id],
      type, // AUDIO,VIDEO
      group_id: null,
      method: 'SINGLE', // SINGLE,MULTILPE,GROUP
    };
    dispatch(
      MsgAction.StartCall(Data, (call) => {
        navigate(call?.id ? ROUTES.CALL_ROOM(call.id) : ROUTES.CALLING);
      })
    );
  };

  const makeVoiceCall = () => placeCall('AUDIO');
  const makeVideoCall = () => placeCall('VIDEO');

  /**
   * Drag-and-drop attach.
   *
   * `dragDepth` is a counter because dragenter/dragleave fire for every child
   * element the pointer crosses — a boolean flickers off the moment the cursor
   * moves from the stream onto a bubble inside it.
   *
   * `dragover` must call `preventDefault` or the browser treats the drop as a
   * navigation and replaces the app with the image.
   */
  const [dragging, setDragging] = useState(false);
  const dragDepth = useRef(0);

  // Only react to files. Dragging selected text, or a message being reordered,
  // carries other types and must not raise a drop target.
  const carriesFiles = (event) => Array.from(event.dataTransfer?.types ?? []).includes('Files');

  const dropHandlers = {
    onDragEnter: (event) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      dragDepth.current += 1;
      setDragging(true);
    },
    onDragOver: (event) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
    },
    onDragLeave: (event) => {
      if (!carriesFiles(event)) return;
      dragDepth.current = Math.max(0, dragDepth.current - 1);
      if (dragDepth.current === 0) setDragging(false);
    },
    onDrop: (event) => {
      if (!carriesFiles(event)) return;
      event.preventDefault();
      dragDepth.current = 0;
      setDragging(false);
      // The drop target is the stream, which lives here, but the attachment tray lives
      // in DirectComposer — so the files are handed over rather than the attachment
      // state being hoisted back up into this component.
      composerRef.current?.acceptFiles(event.dataTransfer.files);
    },
  };

  /** Handle on DirectComposer, used only to forward dropped files into it. */
  const composerRef = useRef(null);

  /**
   * Callbacks handed to DirectComposer. All three are `useCallback`'d so the memo on
   * the composer holds — a fresh identity per render would re-render it on every stream
   * update, which is the cost the extraction exists to remove.
   */
  const clearReply = useCallback(() => setReplyOnMsg(null), []);

  const handleSent = useCallback(() => {
    setUnreadMsg(0);
    setNewMessageRecieve((previous) => !previous);
    setTyping(null);
  }, []);

  const openScheduleModal = useCallback(() => setIsScheduleModalOpen(true), []);

  // useEffect(() => {
  //   if (seenAudio) return
  //   setIsRecording(false);
  //     sendMessage()
  //   clearInterval(intervalRef.current);
  // },[ChatImagesUrl])

  const emitTyping = useCallback(
    (status) => {
      socket?.emit('typing', {
        channel_id: channel_id,
        status,
        name: userData?.chat_member?.user?.first_name,
        user_id: userData?.id,
      });
    },
    [socket, channel_id, userData?.chat_member?.user?.first_name, userData?.id]
  );

  // Debounces the "stopped" signal. A ref rather than a local: the timer has to
  // survive between keystrokes to be cancellable, which is the same mistake the
  // search debounce in the sidebar once made.
  const typingStopTimer = useRef(null);

  /**
   * Stop typing on the way out.
   *
   * Runs when the conversation changes as well as on unmount, so switching chats
   * mid-sentence does not leave the previous channel showing this user as still
   * typing — the pending idle timer would have fired against the new channel_id.
   */
  useEffect(
    () => () => {
      clearTimeout(typingStopTimer.current);
      emitTyping(false);
    },
    [emitTyping]
  );

  const handlePlaySound = () => {
    if (ringtoneRef.current) {
      ringtoneRef.current.currentTime = 0; // Reset playback position
      ringtoneRef.current.play().catch(() => {});
    }
  };

  const ReportModalOpen = (msg) => {
    setModal(true);
    setReportMsgId(msg?.id);
  };
  const EditModalClose = () => {
    setModal(false);
    reset();
    setReportMsgId(null);
  };
  const onSubmitFirstModal = (data) => {
    const Data = {
      description: data?.description,
      type: SelectedChatUser?.chat_member?.user?.id == reportMsgId ? 'USER' : 'MESSAGE',
      id: reportMsgId,
    };
    dispatch(
      MsgAction.ReportMsg(Data, () => {
        setModal(false);
        setReportMsgId(null);
      })
    );
    reset();
  };

  const CloseItem = () => navigate(ROUTES.MESSAGES);

  const handleScheduleMessage = async (event) => {
    // Reached as a form submit (Enter or the button), so the default navigation
    // has to be stopped before anything else happens.
    event?.preventDefault?.();

    /**
     * Validate before emitting. None of this was checked: the only guard was a
     * truthy `scheduleMessage`, so an empty date and time produced
     * `new Date('T')` — an Invalid Date — which went to the server as
     * `scheduled_at`, and a time already in the past was accepted outright.
     */
    const text = scheduleMessage.trim();
    if (!text) {
      Utils.showMessage('warning', 'Write the message you want to schedule.', {
        key: 'schedule-invalid',
      });
      return;
    }
    if (text.length > SCHEDULE_LIMITS.message.max) {
      Utils.showMessage(
        'warning',
        `Keep a scheduled message under ${SCHEDULE_LIMITS.message.max} characters.`,
        { key: 'schedule-invalid' }
      );
      return;
    }
    if (!scheduleDate || !scheduleTime) {
      Utils.showMessage('warning', 'Pick both a date and a time.', { key: 'schedule-invalid' });
      return;
    }

    const scheduledDateTime = new Date(`${scheduleDate}T${scheduleTime}`);
    if (Number.isNaN(scheduledDateTime.getTime())) {
      Utils.showMessage('warning', 'That date and time could not be read.', {
        key: 'schedule-invalid',
      });
      return;
    }
    // The delivery cron runs once a minute, so anything sooner than that is a
    // promise the backend cannot keep — and a past time never fires at all.
    if (scheduledDateTime.getTime() - Date.now() < SCHEDULE_LIMITS.leadMs) {
      Utils.showMessage('warning', 'Choose a time at least a minute from now.', {
        key: 'schedule-invalid',
      });
      return;
    }

    {
      if (!isRealtimeUsable(socket)) {
        Utils.showMessage('error', SOCKET_DOWN_MESSAGE, { key: 'socket-offline' });
        return;
      }

      try {
        socket.emit(
          'message:send',
          {
            sender_id: userData?.id,
            channel_id: channel_id,
            message: scheduleMessage,
            type: 'TEXT',
            media: null,
            current_location: null,
            duration: null,
            scheduled_at: scheduledDateTime,
          },
          (item) => {
            const failure = Utils.ackError(item);
            if (failure) {
              Utils.showMessage('error', failure);
              return;
            }

            Utils.showMessage('success', 'Message scheduled successfully');
            // Reload the conversation so the sender sees their scheduled (pending)
            // message right away — the history API returns the sender's own
            // PENDING scheduled messages, but nothing is pushed live at schedule
            // time (and the later cron delivery is filtered out as an own message).
            // Clear first: the reducer PREPENDS fetched pages, so a bare reload
            // would duplicate the conversation.
            dispatch(MsgAction.ClearChatMsgs());
            dispatch(MsgAction.MessageChatChannel({ id: channel_id, fromApi: true }));
          }
        );
        // Reset the state variables for the scheduled message modal
        setScheduleMessage('');
        setScheduleDate('');
        setScheduleTime('');
        // Close the modal
        setIsScheduleModalOpen(false);
      } catch {}
    }

    setIsScheduleModalOpen(false);
  };

  /**
   * Older history, one page at a time.
   *
   * Everything this replaces was wrong in a different way. `scrollTop == 0` never held
   * on a fractional-DPI display, so older messages could not be loaded at all on some
   * machines. The stop condition was `msgsCount > 10` — a constant for the channel,
   * never compared against what had been loaded — so once the reader reached the
   * beginning, every further scroll fired another request for a page past the end,
   * forever. `offset` was React state read from this closure, so a burst of scroll
   * events all asked for the same page. And nothing held the scroll position, so a
   * prepended page moved the viewport somewhere else in the conversation.
   *
   * The cursor comes from the server and the sentinel sits at the TOP of the stream,
   * because this list pages backwards into the past.
   */
  const fetchOlder = useCallback(
    (cursor) => {
      // Only a cursor page is a prepend. The cursor-less first page REPLACES the
      // stream, and anchoring it made the open position depend on whatever the
      // previous conversation's scroll height happened to be.
      if (cursor) captureAnchor();
      dispatch(MsgAction.MessageChatChannel({ cursor, id: channel_id, fromApi: true }));
    },
    [dispatch, channel_id, captureAnchor]
  );

  const { sentinelRef: olderSentinelRef, exhausted: historyExhausted } = useInfiniteList({
    resetKey: channel_id,
    fetchPage: fetchOlder,
    loading: message_chats_loader,
    loadedCount: PersonalChatMsgs?.length ?? 0,
    nextCursor: messageNextCursor,
    enabled: Boolean(channel_id),
    edge: 'start',
    // Which channel the rows and cursor in the store describe. Switching away while a
    // page is in flight does not cancel it, and every conversation's pages land in the
    // same slice.
    loadedFor: message_chats_channel_id,
  });

  // Opening the panel is all this does now — the effect above owns the fetch.
  const seenMediaImages = () => setRightSideOpen(true);

  const replyMsg = (e) => {
    setReplyOnMsg(e);
  };
  const deleteMsg = (e, index) => {
    const messageId = e?.message_id || e?.id || e?.message_ID;
    setMessageDeleted(e?.id || e?.message_ID);
    dispatch(MsgAction.DeleteMessage(messageId));
    // See GroupMessages: the reducer keys on `message_id` where one is available,
    // because the index shifts as older history prepends.
    dispatch(
      MsgAction.DeleteMessageNow({
        index,
        message_id: e?.id ?? messageId,
        deletedBy: userData?.id,
      })
    );
  };

  /**
   * Star / unstar a message. `is_saved` is the DESIRED state, so the saga never has to
   * re-read the stream to work out which way the toggle went.
   */
  const toggleSaveMsg = (msg) => {
    const messageId = msg?.id || msg?.message_id || msg?.message_ID;
    if (!messageId) return;
    dispatch(MsgAction.ToggleSaveMessage({ message_id: messageId, is_saved: !msg?.is_saved }));
  };

  const handlePlay = (e) => {
    const allAudios = document.querySelectorAll('audio');
    let Current = e.target.currentTime;
    // get all audio elements and stop them all
    allAudios.forEach((audioEl) => {
      if (!audioEl.paused && e.target.id == audioEl.id) {
        e.target.currentTime = Current;
        e.target.play();
      } else {
        audioEl.pause();
        audioEl.currentTime = 0;
      }
    });
  };

  const setNewReactionMessages = (message) => {
    dispatch({ type: MESSAGE_CHAT_CHANNEL_DIRECT_UPDATE, payload: message });
  };

  /**
   * Toggle the current user's reaction on a message.
   *
   * One copy for the pane: this block was inlined identically inside both the
   * incoming and outgoing bubble branches. A user holds at most one reaction, so
   * re-picking the same emoji clears it and picking another replaces it —
   * `emoji_id: null` is the server's remove signal.
   */
  const applyReaction = (msg, emoji) => {
    handlePlaySound();

    const existingReaction = msg?.chat_reactions?.find(
      (reaction) => String(reaction?.user_id) === String(userData?.id)
    );
    const isRemoval = existingReaction?.emoji_id === emoji;
    const emojiIdToSend = isRemoval ? null : emoji;

    socket?.emit(
      'emoji:send',
      {
        senderId: msg?.sender_id,
        channel_id: msg?.channel_id,
        message_id: msg?.id,
        emoji_id: emojiIdToSend,
      },
      (resp) => {
        // Fold the ack through the same reducer as the broadcast — the server
        // returns the message's whole reaction set, so the reactor's pane lands
        // on the same state every other participant is shown.
        setNewReactionMessages(applyReactionEvent(PersonalChatMsgs, resp?.response));
      }
    );
    setCurrentIndex(-1);
  };

  const paneTitleName = SelectedChatUser?.chat_member?.user?.first_name?.replace(/\b\w/g, (char) =>
    char.toUpperCase()
  );

  // Short form on screen, because the header gives this one truncating line —
  // see `formatLastSeenShort`. The exact timestamp is not lost: it is the
  // element's `title`, and the details panel spells it out in full.
  //
  // The presence push wins over the row from the HTTP read: it carries the timestamp
  // written at disconnect, so the header can go from "Online" straight to "Last seen just
  // now" without a refetch. Both are already redacted server-side when the owner has
  // turned Last Seen off, so `is_last_seen_enabled` is belt-and-braces rather than the
  // gate it used to be.
  const lastSeen = is_last_seen_enabled
    ? (lastSeenOf(peerId) ?? selected_chat_user?.chat_member?.user?.last_seen ?? null)
    : null;
  // A missing or malformed timestamp falls through to 'Offline' rather than the
  // dangling "Last seen," the bare interpolation used to leave behind.
  const lastSeenShort = formatLastSeenShort(lastSeen);
  const paneTitleStatus = isUserOnline
    ? 'Online'
    : lastSeenShort
      ? `Last seen ${lastSeenShort}`
      : 'Offline';
  const paneTitleStatusFull = isUserOnline
    ? 'Online'
    : lastSeenShort
      ? `Last seen ${formatLastSeen(lastSeen)}`
      : 'Offline';

  return (
    <>
      {/* Drop target is the whole pane, not just the composer: dropping a photo
          "into the conversation" is the gesture people actually make. */}
      <main className="conversation-pane" {...dropHandlers}>
        {dragging && (
          <div className="composer-drop" aria-hidden="true">
            <span>Drop to attach</span>
          </div>
        )}

        <header className="pane-header">
          <PaneBack onBack={CloseItem} label="Back to conversations" />

          <span
            className="pane-avatar cursor-pointer"
            onClick={() => setRightSideOpen(true)}
            title="Conversation details"
          >
            {SelectedChatUser?.chat_member?.user?.media?.media_url ? (
              <Image
                className="object-cover"
                height={38}
                width={38}
                style={{ borderRadius: 'var(--r-avatar)' }}
                src={SelectedChatUser?.chat_member?.user?.media?.media_url}
                preview={false}
              />
            ) : (
              <PlaceholderAvatar size={38} />
            )}
            <span
              className={`presence-dot ${isUserOnline ? 'is-online' : 'is-offline'}`}
              style={{
                position: 'absolute',
                right: -1,
                bottom: -1,
                width: 12,
                height: 12,
                border: '2.5px solid var(--panel)',
              }}
            />
          </span>

          <div className="pane-title cursor-pointer" onClick={seenMediaImages}>
            <span className="pane-title-name" title={paneTitleName}>
              {paneTitleName}
            </span>
            <span className="pane-title-status">
              <span className={`presence-dot ${isUserOnline ? 'is-online' : 'is-offline'}`} />
              {/* Titled as well as truncated: the short form is what fits on the
                  line, the tooltip is where the exact timestamp still lives. */}
              <span className="pane-title-status-text" title={paneTitleStatusFull}>
                {paneTitleStatus}
              </span>
            </span>
          </div>

          <div style={{ flex: 1 }} />

          <div className="pane-actions">
            {userData?.level != 'LEVEL1' && (
              <>
                <button
                  type="button"
                  title="Voice call"
                  className="chat-action-btn"
                  onClick={makeVoiceCall}
                >
                  <IoCallOutline size={17} />
                </button>
                <button
                  type="button"
                  title="Video call"
                  className="chat-action-btn"
                  onClick={makeVideoCall}
                >
                  <CiVideoOn size={18} />
                </button>
              </>
            )}
            {/* `is-accent` marks the panel as open, so the cluster only ever has
                one highlighted control. */}
            <button
              type="button"
              title="Conversation details"
              aria-pressed={Boolean(rightSideOpen)}
              className={`chat-action-btn ${rightSideOpen ? 'is-accent' : ''}`}
              onClick={() => setRightSideOpen(!rightSideOpen)}
            >
              <MdInfoOutline size={18} />
            </button>
          </div>
        </header>

        <div ref={ScrollingRef} className="pane-stream" data-infinite-root>
          {/* Top of the stream: this list pages BACKWARDS, so the sentinel that asks
              for the next page belongs at the head, not the foot. */}
          {!historyExhausted && <div ref={olderSentinelRef} aria-hidden="true" />}
          {/* Opening a conversation showed nothing at all while its history was in
              flight: this render was commented out, and the flag it read
              (`loading`) is not a key MsgReducer has ever set — the real one was
              the generic `loader`, which is shared with a dozen unrelated
              requests. `message_chats_loader` is scoped to this fetch.

              Centred while the stream is empty; a small marker at the top when
              paging in older messages, so the spinner never covers what is
              already on screen. */}
          {message_chats_loader && (
            <div className={`stream-loading ${PersonalChatMsgs?.length ? 'is-more' : ''}`}>
              <Loader.Circular size={PersonalChatMsgs?.length ? 24 : 40} />
            </div>
          )}
          {PersonalChatMsgs ? (
            PersonalChatMsgs?.map((msg, index) => {
              // Inside the map, so the list exists — `?.length` here only made
              // the arithmetic silently NaN if it somehow did not.
              //
              // Clamped at 0. Unread counts more than a page — 25 unread against 20
              // loaded — put the index off the front of the array, so the `index ===
              // highlightIndex` test below could never hold and the divider silently did
              // not render in exactly the case it is most wanted. At the top of the loaded
              // stream is the honest position: everything on screen is unread.
              const highlightIndex = Math.max(0, PersonalChatMsgs.length - unreadMsg);
              // Oldest-first, so the message before this one is the day to
              // compare against. Null unless this message opens a new day.
              const dayLabel = dayDividerFor(
                msg?.createdAt,
                PersonalChatMsgs[index - 1]?.createdAt
              );
              return (
                // Keyed: this fragment holds up to three siblings per message, and
                // as a bare `<>` React had no identity for any of them — every
                // arriving message re-keyed the whole stream by position.
                <React.Fragment key={msg?.id ?? `idx-${index}`}>
                  <DayDivider label={dayLabel} />

                  {unreadMsg > 0 && index === highlightIndex && (
                    <div className="flex justify-between items-center mb-6">
                      <span
                        className="w-full h-0"
                        style={{ border: '1px solid var(--text)' }}
                      ></span>
                      <Text.Basic
                        className="mx-4 text-[#2B3674] dark:text-white whitespace-nowrap"
                        style={{
                          fontWeight: '700',
                          fontSize: '0.8rem',
                        }}
                        text={`${unreadMsg} Unread Messages`}
                      />
                      <span
                        className="w-full h-0"
                        style={{ border: '1px solid var(--text)' }}
                      ></span>
                    </div>
                  )}
                  {msg.sender_id != userData?.id && !msg?.deleted_by_id ? (
                    // Mirrors the outgoing row: bubble first, reaction trigger
                    // OUTSIDE it. The trigger used to be absolutely pinned inside
                    // the incoming card, which is why it sat on top of the text.
                    <div className="msg-row flex justify-start mb-6">
                      {/* One-to-one conversation: no avatar or sender name on
                          each message — there is exactly one other person and
                          the header already names them. The group pane keeps
                          both, since there it identifies who is speaking. */}
                      <div className="relative msg-bubble message-width">
                        <MessageMenuTooltip
                          placement="rightBottom"
                          menu={
                            msg?.deleted_by_id != userData?.id ? (
                              <MessageMenu
                                onReply={() => replyMsg(msg)}
                                onDelete={
                                  msg?.sender_id != userData?.id
                                    ? () => deleteMsg(msg, index)
                                    : null
                                }
                                isSaved={Boolean(msg?.is_saved)}
                                onToggleSave={msg?.deleted_by_id ? null : () => toggleSaveMsg(msg)}
                                onReport={
                                  msg?.sender_id != userData?.id ? () => ReportModalOpen(msg) : null
                                }
                              />
                            ) : (
                              ''
                            )
                          }
                        >
                          <div
                            className={`${
                              msg?.reply_id ? 'border-4 border-[#f5f8ff] dark:border-[#353535]' : ''
                            } rounded-xl bg-[#f5f8ff] dark:bg-[#353535] px-4 py-2`}
                          >
                            {msg?.reply_id ? (
                              <div className="w-full p-2 border border-[#f5f8ff] dark:border-[#2a2a2a] rounded-lg bg-white dark:bg-[#2a2a2a]">
                                <Text.Basic
                                  style={{
                                    fontWeight: '800',
                                    fontSize: '0.9rem',
                                  }}
                                  className="text-[#2B3674] dark:text-white"
                                  text={msg?.replied_message?.user?.first_name?.replace(
                                    /\b\w/g,
                                    (char) => char.toUpperCase()
                                  )}
                                />
                                <Col span={24}>
                                  {msg?.replied_message?.type === 'TEXT' ? (
                                    <Text.Basic
                                      style={{
                                        fontWeight: '400',
                                        fontSize: '0.8rem',
                                      }}
                                      className="text-[#2B3674] dark:text-gray-200"
                                      text={msg?.replied_message?.message}
                                    />
                                  ) : msg?.replied_message?.duration ? (
                                    <span>
                                      <Avatar size={30} shape="square" icon={<GiSoundWaves />} />
                                      <Text.Basic
                                        style={{
                                          fontWeight: '400',
                                          fontSize: '0.8rem',
                                        }}
                                        className="text-[#2B3674] ml-2"
                                        text={'Audio'}
                                      />
                                    </span>
                                  ) : (
                                    <span>
                                      <Avatar size={20} shape="square" icon={<UserOutlined />} />
                                      <Text.Basic
                                        style={{
                                          fontWeight: '400',
                                          fontSize: '0.8rem',
                                        }}
                                        className="text-[#2B3674] ml-2"
                                        text={'MEDIA'}
                                      />
                                    </span>
                                  )}
                                </Col>
                              </div>
                            ) : (
                              ''
                            )}
                            {msg?.type === 'TEXT'
                              ? msg?.deleted_by_id != userData?.id && (
                                  <div className={`${msg?.reply_id ? 'bg-[#f5f8ff]' : ''}`}>
                                    <MessageText
                                      style={{
                                        fontWeight: '400',
                                        fontSize: '0.8rem',
                                      }}
                                      className="text-[#2B3674] dark:text-white"
                                      text={msg?.message}
                                    />
                                  </div>
                                )
                              : msg?.deleted_by_id != userData?.id &&
                                msg?.media?.map((msg) =>
                                  msg?.format?.startsWith('audio/') ? (
                                    <ReactAudioPlayer
                                      src={msg?.media_url}
                                      autoPlay={false}
                                      controls
                                      onPlay={(e) => handlePlay(e, index)}
                                      id={index}
                                    />
                                  ) : msg?.format?.startsWith('video/') ? (
                                    <ReactPlayer
                                      height={200}
                                      width={200}
                                      url={msg?.media_url}
                                      controls={true}
                                    />
                                  ) : (
                                    <Image
                                      className="rounded-xl object-cover"
                                      // Thumbnail in the bubble, original only when opened.
                                      // This pulled the full-resolution upload to fill a
                                      // 200px box — several MB per photo message.
                                      src={msg?.media_thumb_url || msg?.media_url}
                                      preview={{ src: msg?.media_url }}
                                      width={200}
                                      height={200}
                                      loading="lazy"
                                      decoding="async"
                                    />
                                  )
                                )}
                            {/* Sent time lives inside the box: it used to
                                        sit in a header row above the bubble, which
                                        is why the stream read as three stacked
                                        elements per message instead of one card. */}
                            <div className="msg-meta">
                              {new Date(msg?.createdAt).toLocaleTimeString([], {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              })}
                            </div>
                          </div>
                        </MessageMenuTooltip>
                        <MessageReactions
                          reactions={msg?.chat_reactions}
                          currentUserId={userData?.id}
                          onOpenList={reactedListingModal}
                        />
                      </div>
                      <ReactionPicker
                        emojis={EmojiLists}
                        reactions={msg?.chat_reactions}
                        currentUserId={userData?.id}
                        isOpen={currentIndex === index}
                        onToggle={() => setCurrentIndex(currentIndex === index ? -1 : index)}
                        onClose={() => setCurrentIndex(-1)}
                        onSelect={(emoji) => applyReaction(msg, emoji)}
                        align="right"
                        className="ml-2 shrink-0"
                      />
                      <audio ref={ringtoneRef} src={emojiSound} />
                    </div>
                  ) : (
                    <>
                      {!msg?.deleted_by_id && (
                        <div className="msg-row flex justify-end mb-6 relative">
                          <ReactionPicker
                            emojis={EmojiLists}
                            reactions={msg?.chat_reactions}
                            currentUserId={userData?.id}
                            isOpen={currentIndex === index}
                            onToggle={() => setCurrentIndex(currentIndex === index ? -1 : index)}
                            onClose={() => setCurrentIndex(-1)}
                            onSelect={(emoji) => applyReaction(msg, emoji)}
                            align="left"
                            className="ml-2 mr-2"
                          />
                          <div
                            // `is-out` carries no styling — it tells the motion
                            // layer to enter this bubble from the sender's side.
                            // `is-pending` / `is-failed` are the optimistic send's
                            // two unsettled states.
                            className={`text-end rounded-xl px-4 py-1 mr-2 msg-bubble is-out message-width relative h-fit${
                              msg?.failed ? ' is-failed' : msg?.pending ? ' is-pending' : ''
                            }`}
                            style={{
                              backgroundColor: Colors.MainColor,
                            }}
                          >
                            <div className="flex justify-start">
                              <MessageMenuTooltip
                                placement="leftBottom"
                                menu={
                                  msg?.deleted_by_id != userData?.id ? (
                                    <MessageMenu
                                      onReply={() => replyMsg(msg)}
                                      onDelete={
                                        msg?.sender_id == userData?.id
                                          ? () => deleteMsg(msg, index)
                                          : null
                                      }
                                      isSaved={Boolean(msg?.is_saved)}
                                      onToggleSave={
                                        msg?.deleted_by_id ? null : () => toggleSaveMsg(msg)
                                      }
                                    />
                                  ) : (
                                    ''
                                  )
                                }
                              >
                                <div
                                  className={`${
                                    msg?.reply_id && msg?.type == 'TEXT'
                                      ? 'border-4'
                                      : msg?.reply_id && msg?.type == 'MEDIA'
                                        ? 'border-8 rounded-xl'
                                        : ''
                                  } ${msg?.reply_id ? `border-[#f56469]` : ''}  `}
                                >
                                  {msg?.reply_id ? (
                                    <div
                                      className={`w-full p-2 border-b-4 border-[#f56469] rounded-lg bg-white dark:bg-[#2a2a2a]`}
                                    >
                                      <Text.Basic
                                        style={{
                                          fontWeight: '800',
                                          fontSize: '0.9rem',
                                        }}
                                        className="text-[#2B3674] dark:text-white"
                                        text={msg?.replied_message?.user?.first_name?.replace(
                                          /\b\w/g,
                                          (char) => char.toUpperCase()
                                        )}
                                      />
                                      <Col span={24}>
                                        {msg?.replied_message?.type === 'TEXT' ? (
                                          <Text.Basic
                                            style={{
                                              fontWeight: '400',
                                              fontSize: '0.8rem',
                                            }}
                                            className="text-[#2B3674] dark:text-gray-200"
                                            text={msg?.replied_message?.message}
                                          />
                                        ) : msg?.replied_message?.duration ? (
                                          <span>
                                            <Avatar
                                              size={30}
                                              shape="square"
                                              icon={<GiSoundWaves />}
                                            />
                                            <Text.Basic
                                              style={{
                                                fontWeight: '400',
                                                fontSize: '0.8rem',
                                              }}
                                              className="text-[#2B3674] ml-2"
                                              text={'Audio'}
                                            />
                                          </span>
                                        ) : (
                                          <span>
                                            <Avatar
                                              size={20}
                                              shape="square"
                                              icon={<UserOutlined />}
                                            />
                                            <Text.Basic
                                              style={{
                                                fontWeight: '400',
                                                fontSize: '0.8rem',
                                              }}
                                              className="text-[#2B3674] ml-2"
                                              text={'MEDIA'}
                                            />
                                          </span>
                                        )}
                                      </Col>
                                    </div>
                                  ) : (
                                    ''
                                  )}
                                  {msg?.type === 'TEXT' ? (
                                    msg?.deleted_by_id != userData?.id ? (
                                      <div
                                        className={`${
                                          msg?.reply_id ? `bg-${Colors.MainColor}` : ''
                                        } flex`}
                                      >
                                        <MessageText
                                          style={{
                                            fontWeight: '400',
                                            fontSize: '0.8rem',
                                          }}
                                          className={`text-white `}
                                          text={msg?.message}
                                          onAccent
                                        />
                                      </div>
                                    ) : (
                                      <Text.Basic
                                        style={{
                                          fontWeight: '400',
                                          fontSize: '0.6rem',
                                        }}
                                        className="text-[#2B3674]"
                                        text={'This message has been deleted'}
                                      />
                                    )
                                  ) : msg?.deleted_by_id != userData?.id ? (
                                    msg?.media?.map((msg, index) =>
                                      msg?.format?.startsWith('audio/') ? (
                                        <ReactAudioPlayer
                                          src={msg?.media_url}
                                          autoPlay={false}
                                          controls
                                          onPlay={(e) => handlePlay(e, index)}
                                          id={index}
                                        />
                                      ) : msg?.format?.startsWith('video/') ? (
                                        <ReactPlayer
                                          height={200}
                                          width={200}
                                          url={msg?.media_url}
                                          controls={true}
                                        />
                                      ) : (
                                        <Image
                                          className="rounded-xl object-cover"
                                          width={200}
                                          height={200}
                                          // preview={false}
                                          src={msg?.media_url}
                                        />
                                      )
                                    )
                                  ) : (
                                    <Text.Basic
                                      style={{
                                        fontWeight: '400',
                                        fontSize: '0.6rem',
                                      }}
                                      className="text-[#2B3674]"
                                      text={'This message has been deleted'}
                                    />
                                  )}
                                </div>
                              </MessageMenuTooltip>
                            </div>
                            <div className="msg-meta is-on-accent">
                              {/* Pending scheduled messages show the SCHEDULED send
                                  time (not their creation time) + a marker, so the
                                  sender isn't misled into thinking it's already sent. */}
                              {msg?.scheduled_message?.status === 'PENDING'
                                ? `⏱ Scheduled · ${new Date(
                                    msg?.scheduled_message?.scheduled_at
                                  ).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true,
                                  })}`
                                : new Date(msg?.createdAt).toLocaleTimeString([], {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true,
                                  })}
                              {/* Ticks replace the "· Seen" text that used to sit
                                  here, and appear on every outgoing message
                                  rather than only the newest: the read state is
                                  computed per message from the same receipt
                                  timestamp, so there was never a reason to show
                                  it once. A scheduled message that has not fired
                                  yet has no delivery state at all. */}
                              {msg?.scheduled_message?.status !== 'PENDING' && (
                                <MessageTicks
                                  state={
                                    msg?.failed
                                      ? 'failed'
                                      : msg?.pending
                                        ? 'pending'
                                        : othersReadAt &&
                                            new Date(othersReadAt) >= new Date(msg?.createdAt)
                                          ? 'read'
                                          : 'sent'
                                  }
                                />
                              )}
                            </div>
                            <MessageReactions
                              reactions={msg?.chat_reactions}
                              currentUserId={userData?.id}
                              isOut
                              floating
                              onOpenList={reactedListingModal}
                            />
                          </div>
                        </div>
                      )}
                    </>
                  )}
                </React.Fragment>
              );
            })
          ) : (
            // Bubble shapes rather than a spinner — the stream's layout is known
            // before its contents are.
            <Skeleton.Stream rows={5} />
          )}

          {/* Last thing in the stream, so it sits under the newest message and
              `lastMessageRef` scrolls it into view like any other arrival. */}
          {isPeerTyping && <TypingIndicator />}

          <div ref={lastMessageRef} />
        </div>

        {/* Pinned to the pane rather than the stream — see .stream-jump. Keyed on
            the message count, which is all it needs to know that something
            arrived while the reader was up in the history. */}
        <ScrollToLatest scrollRef={ScrollingRef} newestKey={PersonalChatMsgs?.length ?? 0} />

        <div className="pane-composer">
          <Col span={24}>
            {/* Draft text, attachments and the voice note live in DirectComposer now.
                They used to be state on THIS component — the one that renders the
                message stream — so every keystroke, and every tick of the recording
                timer, re-rendered every loaded bubble. See the note in DirectComposer. */}
            <DirectComposer
              ref={composerRef}
              channelId={channel_id}
              userData={userData}
              socket={socket}
              replyOnMsg={replyOnMsg}
              onClearReply={clearReply}
              emitTyping={emitTyping}
              onSent={handleSent}
              onOpenSchedule={openScheduleModal}
            />
          </Col>
        </div>
      </main>

      {rightSideOpen && (
        <aside className="info-panel is-open">
          <InfoHeader onClose={() => setRightSideOpen(false)} />

          <InfoBody onScroll={handleMediaScroll}>
            <InfoIdentity
              avatar={
                SelectedChatUser?.chat_member?.user?.media?.media_url ? (
                  <img
                    className="avatar-circle"
                    src={SelectedChatUser?.chat_member?.user?.media?.media_url}
                    alt=""
                    style={{ width: 76, height: 76 }}
                  />
                ) : (
                  <Avatar size={76} icon={<UserOutlined />} />
                )
              }
              name={SelectedChatUser?.chat_member?.user?.first_name?.replace(/\b\w/g, (char) =>
                char.toUpperCase()
              )}
              sub={SelectedChatUser?.chat_member?.user?.position}
              status={
                <InfoStatusPill online={isUserOnline}>
                  {isUserOnline
                    ? 'Online'
                    : is_last_seen_enabled
                      ? `Last seen ${formatLastSeen(selected_chat_user?.chat_member?.user?.last_seen)}`
                      : 'Offline'}
                </InfoStatusPill>
              }
            />

            <InfoActions>
              <InfoAction icon={<IoCallOutline size={17} />} label="Call" onClick={makeVoiceCall} />
              <InfoAction icon={<CiVideoOn size={18} />} label="Video" onClick={makeVideoCall} />
              {/* Reporting someone is the destructive option in this row, so it
                  is the one that shifts colour on hover. */}
              <InfoAction
                icon={<MdReportProblem size={17} />}
                label="Report"
                tone="danger"
                onClick={() => ReportModalOpen(SelectedChatUser?.chat_member?.user)}
              />
            </InfoActions>

            {SelectedChatUser?.chat_member?.user?.login_id && (
              <InfoField label="Login ID">
                <InfoReadout>
                  <span style={{ fontFamily: 'var(--font-mono)' }}>
                    {SelectedChatUser.chat_member.user.login_id}
                  </span>
                </InfoReadout>
              </InfoField>
            )}

            <InfoSection
              label="Shared media"
              count={shareMedia?.length ?? 0}
              open={showHideMedia}
              onToggle={() => setShowHideMedia(!showHideMedia)}
            >
              <MediaGrid
                loading={shareMediaLoading && !shareMedia?.length}
                isEmpty={!shareMedia?.length}
                emptyText="Nothing shared in this conversation yet."
              >
                {shareMedia?.map((item, mediaIndex) => (
                  <MediaTile
                    key={item?.id ?? mediaIndex}
                    label={
                      item?.format?.startsWith('audio/')
                        ? 'AUDIO'
                        : item?.format?.startsWith('video/')
                          ? 'VIDEO'
                          : null
                    }
                  >
                    {item?.format?.startsWith('image/') ? (
                      <Image
                        src={item?.media_thumb_url}
                        preview={{ src: item?.media_url }}
                        loading="lazy"
                        decoding="async"
                      />
                    ) : item?.format?.startsWith('audio/') ? (
                      <FaAudioDescription size={26} />
                    ) : item?.format?.startsWith('video/') ? (
                      <video src={item?.media_url} muted playsInline />
                    ) : null}
                  </MediaTile>
                ))}
              </MediaGrid>
            </InfoSection>
          </InfoBody>
        </aside>
      )}

      {/* Report Msg Modal */}
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={modal}
            cancelModal={() => EditModalClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            Children={
              <>
                <div className={`flex justify-between w-full items-center mb-6`}>
                  <IoChevronBackCircle color={Colors.White} size={30} />
                  <Text.Basic
                    className="flex-auto text-center text-[#2B3674] dark:text-white"
                    style={{ fontWeight: '600' }}
                    text={
                      SelectedChatUser?.chat_member?.user?.id == reportMsgId
                        ? 'Report User'
                        : 'Report Message'
                    }
                  />
                  <MdCancel
                    className="cursor-pointer"
                    color={Colors.ModalCancelColor}
                    size={30}
                    onClick={() => EditModalClose()}
                  />
                </div>

                <form onSubmit={handleSubmit(onSubmitFirstModal)} className="w-11/12 mx-auto">
                  <label className="text-[#2B3674] dark:text-white">Report Description</label>
                  <Controller
                    name="description"
                    control={control}
                    /**
                     * Rules, not just HTML attributes. The `minLength`/`required`
                     * attributes below were the only validation here and RHF had
                     * no rules at all — so `fieldState.error` never populated and
                     * the error paragraph under the field could never render.
                     * Counted trimmed, because the backend trims before its own
                     * length check.
                     */
                    rules={{
                      validate: (value) => {
                        const text = (value ?? '').trim();
                        if (!text) return 'Describe what is wrong with this message';
                        if (text.length < REPORT_LIMITS.description.min) {
                          return `Give us at least ${REPORT_LIMITS.description.min} characters — "spam" alone is not actionable`;
                        }
                        return (
                          text.length <= REPORT_LIMITS.description.max ||
                          `Keep it under ${REPORT_LIMITS.description.max} characters`
                        );
                      },
                    }}
                    render={({ field, fieldState }) => (
                      <Col span={24} className="relative mb-2">
                        <textarea
                          required
                          maxLength={REPORT_LIMITS.description.max}
                          style={{ resize: 'none' }}
                          rows={4}
                          className="w-full px-2 py-3 rounded outline-0 bg-[#EEF9FE] dark:bg-[#353535] dark:text-white"
                          {...field}
                          value={field.value ?? ''}
                        />
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            gap: 10,
                            marginTop: 4,
                          }}
                        >
                          <span style={{ color: Colors.Error, fontSize: 12 }}>
                            {fieldState.error?.message ?? ''}
                          </span>
                          <span
                            style={{
                              fontSize: 11.5,
                              fontWeight: 600,
                              fontVariantNumeric: 'tabular-nums',
                              color: 'var(--muted)',
                            }}
                          >
                            {(field.value ?? '').length} / {REPORT_LIMITS.description.max}
                          </span>
                        </div>
                      </Col>
                    )}
                  />

                  <Row>
                    <input
                      type="submit"
                      value="Report Now"
                      className="w-full mt-6 ml-2 h-11 rounded cursor-pointer hover:opacity-95"
                      style={{
                        backgroundColor: 'var(--accent)',
                        color: Colors.White,
                      }}
                    />
                  </Row>
                </form>
              </>
            }
          />
        </Col>
      </Row>
      <Row className="" justify={'center'} align={'center'}>
        <Col span={24} className="flex justify-center">
          <CustomModal
            open={reactedListModal}
            cancelModal={() => reactedMembersListClose()}
            className={'custom-modal'}
            centered={true}
            destroyOnClose={true}
            width={450}
            // title={<p className='text-center text-[#2B3674]'>Change Password</p>}
            Children={
              <>
                <div className={`flex items-center justify-center w-full mb-3`}>
                  <Text.Basic
                    className="text-center text-[#2B3674] dark:text-white"
                    style={{ fontWeight: '600' }}
                    text={'Total Reactions'}
                  />
                </div>
                {/* {membersList?.length > 0 && ( */}
                <ul className=" rounded-lg w-full">
                  <li style={{ cursor: 'pointer', padding: '5px 0' }}>
                    <div className="my-members-scroll">
                      {reactedMembersList?.map((user) => (
                        //  <div className="my-2 flex">
                        //  {user?.user?.media?.media_url ? (<Image preview={false} src={user?.user?.media?.media_url} className="mr-2 rounded-full" width={30} height={30} />) : (<Avatar shape="circle" size={32} icon={<UserOutlined />} />)}
                        //  <div className="flex items-center ml-4"><Text.Basic className='text-center text-[#2B3674]' style={{ fontWeight: '200' }} text={user?.user?.first_name} /></div>
                        //  </div>
                        <Row className="py-2 px-4 mb-1 flex items-center bg-[#EEF9FE] dark:bg-[#353535]">
                          <div className="flex items-center justify-center">
                            {user?.emoji_id ? (
                              <span className="text-3xl leading-none">{user?.emoji_id}</span>
                            ) : (
                              <Avatar shape="square" size={40} icon={<UserOutlined />} />
                            )}
                          </div>
                          <Text.Basic
                            style={{ fontWeight: '800', fontSize: '0.8rem' }}
                            className="text-[#2B3674] dark:text-white ml-2"
                            text={user?.reactor?.first_name?.replace(/\b\w/g, (char) =>
                              char.toUpperCase()
                            )}
                          />
                        </Row>
                      ))}
                    </div>
                  </li>
                </ul>
                {/* )} */}
              </>
            }
          />
        </Col>
      </Row>
      {/* Schedule message modal */}
      <CustomModal
        open={isScheduleModalOpen}
        cancelModal={() => setIsScheduleModalOpen(false)}
        centered={true}
        destroyOnClose={true}
        width={450}
        Children={
          <>
            {/* Header */}
            <Text.Basic
              className="flex-auto text-center text-black dark:text-white"
              style={{ fontWeight: '600' }}
              text="Schedule Message"
            />

            {/* A form, so Enter in the date, time or message field schedules —
                the three inputs previously had no submit path and the button was
                the only way through. */}
            <form className="w-11/12 mx-auto mt-4 space-y-4" onSubmit={handleScheduleMessage}>
              {/* Date Picker */}
              <div>
                <label className="block text-black/80 dark:text-white/80 font-medium mb-1">
                  Select Date
                </label>
                <input
                  type="date"
                  required
                  // Yesterday is not a schedule. The picker refuses past dates
                  // outright, which is a restriction the submit check should never
                  // have to catch — though it still does, for a typed-in value.
                  min={new Date().toLocaleDateString('en-CA')}
                  className="w-full px-3 h-11 rounded border border-gray-300 dark:border-[#404040] dark:bg-[#353535] dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  value={scheduleDate}
                  onChange={(e) => setScheduleDate(e.target.value)}
                />
              </div>

              {/* Time Picker */}
              <div>
                <label className="block text-black/80 dark:text-white/80 font-medium mb-1">
                  Select Time
                </label>
                <input
                  type="time"
                  required
                  className="w-full px-3 h-11 rounded border border-gray-300 dark:border-[#404040] dark:bg-[#353535] dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  value={scheduleTime}
                  onChange={(e) => setScheduleTime(e.target.value)}
                />
              </div>

              {/* Message Textarea */}
              <div>
                <label className="block text-black/80 dark:text-white/80 font-medium mb-1">
                  Message
                </label>
                <textarea
                  placeholder="Enter your message"
                  rows={4}
                  required
                  maxLength={SCHEDULE_LIMITS.message.max}
                  value={scheduleMessage}
                  className="w-full px-3 py-2 rounded border border-gray-300 dark:border-[#404040] dark:bg-[#353535] dark:text-white focus:outline-none focus:ring-2 focus:ring-red-500"
                  onChange={(e) => setScheduleMessage(e.target.value)}
                />
                <div
                  style={{
                    marginTop: 4,
                    textAlign: 'right',
                    fontSize: 11.5,
                    fontWeight: 600,
                    fontVariantNumeric: 'tabular-nums',
                    color:
                      scheduleMessage.length >= SCHEDULE_LIMITS.message.max
                        ? 'var(--danger)'
                        : 'var(--muted)',
                  }}
                >
                  {scheduleMessage.length} / {SCHEDULE_LIMITS.message.max}
                </div>
              </div>

              {/* Buttons */}
              <Row className="mt-6">
                <Col span={12}>
                  <input
                    type="button"
                    value="Cancel"
                    className="w-11/12 mr-2 h-11 rounded cursor-pointer border border-gray-500 text-gray-500 hover:opacity-95"
                    onClick={() => setIsScheduleModalOpen(false)}
                  />
                </Col>
                <Col span={12}>
                  <input
                    type="submit"
                    value="Submit"
                    className="w-11/12 ml-2 h-11 rounded cursor-pointer bg-red-500 text-white hover:opacity-95"
                  />
                </Col>
              </Row>
            </form>
          </>
        }
      />
    </>
  );
};
export default MessagesChat;
